// Embeddable content preview: renders a PreviewModel with the renderer for its surface, in one
// context, at desktop or mobile width. Used by the full-page preview shell and inline elsewhere.
import type { PreviewDevice, PreviewModel } from "../../../../shared/content-preview";
import { resolveContext, resolveRenderer } from "./registry";

export const DEVICE_WIDTHS: Record<PreviewDevice, number> = { desktop: 1120, mobile: 390 };

interface Props {
  model: PreviewModel;
  /** Overrides the model's surface when picking a renderer. */
  surface?: string;
  context?: string;
  device?: PreviewDevice;
  className?: string;
}

export function ContentPreview({ model, surface, context, device = "desktop", className }: Props) {
  const renderer = resolveRenderer(surface ?? model.surface);
  const active = resolveContext(renderer, context);
  return (
    <div
      className={`mx-auto w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm ${className ?? ""}`}
      style={{ maxWidth: DEVICE_WIDTHS[device] }}
      data-testid="content-preview"
      data-renderer={renderer.id}
      data-context={active}
      data-device={device}
    >
      {renderer.render(model, active, device)}
    </div>
  );
}

const TONE: Record<string, string> = {
  not_approved: "border-amber-200 bg-amber-50 text-amber-900",
  missing_media: "border-slate-200 bg-slate-50 text-slate-700",
  stale_card: "border-red-200 bg-red-50 text-red-800",
  no_content: "border-slate-200 bg-slate-50 text-slate-700",
};

export function PreviewBanners({ model }: { model: PreviewModel }) {
  if (!model.warnings.length) return null;
  return (
    <ul className="space-y-1" data-testid="preview-banners">
      {model.warnings.map((warning) => (
        <li key={warning.code} role="status" className={`rounded-md border px-3 py-2 text-sm ${TONE[warning.code] ?? TONE.no_content}`} data-warning={warning.code}>
          {warning.message}
        </li>
      ))}
    </ul>
  );
}
